import { useMutation, useQueryClient } from "@tanstack/react-query";
import { queueApi } from "@bw/shared/api";
import type { PartyInviteResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * Invite a checked-in player to a party (POST
 * /queue/party/{partyId}/invites). The member-visible `InvitePicker` is the
 * only PartyDetailPage consumer.
 *
 * The mutation takes the invitee's user id; the inviter is the token user
 * (server-derived). On settlement `party.detail(partyId)` (roster/seat view),
 * `party.sentInvites(partyId)` (the "Invited (pending)" list), AND the
 * `party.inviteCandidates(partyId, *)` prefix (every q-variant of the
 * InvitePicker's candidate cache) are invalidated — the invitee drops out of
 * the candidates list on its next fetch (server-side exclusion, Task 5)
 * without the FE re-deriving the exclusion itself. Errors are NOT swallowed —
 * a 403 (blocked / not a member), 409 (duplicate / full / party not open), or
 * 404 (party gone) surfaces on `mutation.error` for the page to map.
 */
export function useInviteToParty(partyId: string) {
  const queryClient = useQueryClient();

  return useMutation<PartyInviteResponse, unknown, string>({
    mutationFn: (inviteeUserId: string) =>
      queueApi.inviteToParty(partyId, inviteeUserId),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.party.detail(partyId),
        refetchType: "active",
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.party.sentInvites(partyId),
        refetchType: "active",
      });
      // Prefix match (no `q`) invalidates every search-term cache slot at once.
      void queryClient.invalidateQueries({
        queryKey: ["party", "invite-candidates", partyId],
        refetchType: "active",
      });
    },
  });
}
